export interface BillingDraftCompany {
  siren?: string
  country?: string
  legalEntityType?: string
  companyName?: string
  companyTitle?: string
  address?: string
  postalCode?: string
  city?: string
  companyType?: string
  birthDate?: string
  birthCity?: string
  birthCountry?: string
  capital?: string
  registreLocation?: string
}

/** Champs onboarding freelance mappés vers {@link BillingDraftCompany}. */
export interface FreelancerOnboardingBillingSource {
  siren_number?: string
  country?: string
  legal_entity_type?: string
  company_name?: string
  company_title?: string
  address?: string
  postal_code?: string
  city?: string
  company_type?: string
  birth_date?: string
  birth_city?: string
  birth_country?: string
  capital?: string
  registre_location?: string
}

const DEFAULT_COUNTRY = 'France'

export function buildBillingDraftCompanyFromOnboarding(
  source: FreelancerOnboardingBillingSource,
): BillingDraftCompany {
  return {
    siren: source.siren_number,
    country: source.country || DEFAULT_COUNTRY,
    legalEntityType: source.legal_entity_type,
    companyName: source.company_name,
    companyTitle: source.company_title,
    address: source.address,
    postalCode: source.postal_code,
    city: source.city,
    companyType: source.company_type,
    birthDate: source.birth_date,
    birthCity: source.birth_city,
    birthCountry: source.birth_country,
    capital: source.capital,
    registreLocation: source.registre_location,
  }
}

export function isFranceBillingDraftCountry(country?: string | null): boolean {
  const normalized = (country || DEFAULT_COUNTRY).trim().toLowerCase()
  return normalized === 'france' || normalized === 'fr'
}

export function mergeBillingDraftCompany(
  base: BillingDraftCompany | null | undefined,
  override: Partial<BillingDraftCompany> | null | undefined,
): BillingDraftCompany | undefined {
  if (!base && !override) return undefined
  return { ...base, ...override }
}

function appendBillingDraftFields(
  draft: BillingDraftCompany,
  params: Record<string, string>,
): void {
  if (draft.siren) params.siren = draft.siren
  if (draft.country) params.country = draft.country
  if (draft.legalEntityType) params.legalEntityType = draft.legalEntityType
  if (draft.companyName) params.companyName = draft.companyName
  if (draft.companyTitle) params.companyTitle = draft.companyTitle
  if (draft.address) params.address = draft.address
  if (draft.postalCode) params.postalCode = draft.postalCode
  if (draft.city) params.city = draft.city
  if (draft.companyType) params.companyType = draft.companyType
  if (draft.birthDate) params.birthDate = draft.birthDate
  if (draft.birthCity) params.birthCity = draft.birthCity
  if (draft.birthCountry) params.birthCountry = draft.birthCountry
  if (draft.capital) params.capital = draft.capital
  if (draft.registreLocation) params.registreLocation = draft.registreLocation
}

export function buildBillingDraftQueryParams(
  draft: BillingDraftCompany | null | undefined,
): Record<string, string> | undefined {
  if (!draft) return undefined

  const params: Record<string, string> = {}
  appendBillingDraftFields(draft, params)

  return Object.keys(params).length > 0 ? params : undefined
}

export function buildBillingDraftBody(
  draft: BillingDraftCompany | null | undefined,
): Record<string, string> | undefined {
  return buildBillingDraftQueryParams(draft)
}

/** Stable React Query key segment for onboarding draft (sorted JSON). */
export function serializeBillingDraftForQueryKey(
  draft: BillingDraftCompany | null | undefined,
): string | null {
  const params = buildBillingDraftQueryParams(draft)
  if (!params) return null
  const sortedKeys = Object.keys(params).sort()
  const stable: Record<string, string> = {}
  for (const key of sortedKeys) {
    stable[key] = params[key]
  }
  return JSON.stringify(stable)
}
